import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  XMarkIcon, 
  CloudArrowUpIcon,
  DocumentTextIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface Lead {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  leadUid?: string;
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  isRequired: boolean;
  maxFileSizeMb: number;
  allowedFileTypes: string[];
}

interface DocumentTypeGroup {
  id: number;
  name: string;
  description?: string;
  color: string;
  document_types: { id: number; name: string }[];
}

interface FileUpload {
  id: string;
  file: File;
  error?: string;
}

interface GridCell {
  leadId: number;
  documentTypeId: number;
  files: FileUpload[];
}

interface SingleUpload {
  docTypeId: number;
  files: FileUpload[];
}

interface DirectDocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preSelectedLeadId?: number;
  preSelectedLeadName?: string;
  lockLeadSelection?: boolean;
  mode?: 'single' | 'bulk';
}

const UPLOAD_SOURCES = [
  { value: 'manual', label: 'Manual' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'other', label: 'Other' }
];

const MAX_BULK_LEADS = 10;

const generateFileId = () => Math.random().toString(36).substr(2, 9);

const DirectDocumentUploadModal: React.FC<DirectDocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedLeadId,
  preSelectedLeadName,
  lockLeadSelection = false,
  mode: initialMode = 'single'
}) => {
  const [mode, setMode] = useState<'single' | 'bulk'>(initialMode);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentGroups, setDocumentGroups] = useState<DocumentTypeGroup[]>([]);
  
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  const [selectedDocTypeIds, setSelectedDocTypeIds] = useState<number[]>([]);
  const [gridCells, setGridCells] = useState<GridCell[]>([]);
  const [singleUploads, setSingleUploads] = useState<SingleUpload[]>([]);
  
  const [uploadSource, setUploadSource] = useState('manual');
  const [autoVerify, setAutoVerify] = useState(false);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);

  const leadDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchLeads();
      fetchDocumentTypes();
      fetchDocumentGroups();
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (preSelectedLeadId && leads.length > 0) {
      const lead = leads.find(l => l.id === preSelectedLeadId);
      if (lead && !selectedLeads.find(l => l.id === lead.id)) {
        setSelectedLeads([lead]);
      }
    }
  }, [preSelectedLeadId, leads]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (leadDropdownRef.current && !leadDropdownRef.current.contains(e.target as Node)) {
        setShowLeadDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mode === 'bulk' && selectedLeads.length > 0 && selectedDocTypeIds.length > 0) {
      setGridCells(prev => {
        const cells: GridCell[] = [];
        selectedLeads.forEach(lead => {
          selectedDocTypeIds.forEach(docTypeId => {
            const existing = prev.find(c => c.leadId === lead.id && c.documentTypeId === docTypeId);
            cells.push(existing || { leadId: lead.id, documentTypeId: docTypeId, files: [] });
          });
        });
        return cells;
      });
    }
  }, [mode, selectedLeads, selectedDocTypeIds]);

  useEffect(() => {
    if (mode === 'single' && selectedDocTypeIds.length > 0) {
      setSingleUploads(prev => {
        return selectedDocTypeIds.map(docTypeId => {
          const existing = prev.find(u => u.docTypeId === docTypeId);
          return existing || { docTypeId, files: [] };
        });
      });
    }
  }, [mode, selectedDocTypeIds]);

  const resetForm = () => {
    setMode(lockLeadSelection ? 'single' : initialMode);
    setUploadSource('manual');
    setAutoVerify(false);
    setError(null);
    setSuccess(false);
    setSelectedDocTypeIds([]);
    setGridCells([]);
    setSingleUploads([]);
    setUploadProgress({ current: 0, total: 0 });
    if (!preSelectedLeadId) {
      setSelectedLeads([]);
      setLeadSearchTerm('');
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await import('../services/api').then(module => 
        module.leadsAPI.getLeads()
      );
      setLeads(response.data || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await import('../services/api').then(module => 
        module.default.get('/documents/types')
      );
      setDocumentTypes(response.data.data || []);
    } catch (err) {
      console.error('Error fetching document types:', err);
    }
  };

  const fetchDocumentGroups = async () => {
    try {
      const response = await import('../services/api').then(module => 
        module.default.get('/documents/groups')
      );
      setDocumentGroups(response.data.data || []);
    } catch (err) {
      console.error('Error fetching document groups:', err);
    }
  };

  const handleGroupSelect = (group: DocumentTypeGroup) => {
    const groupDocTypeIds = group.document_types.map(dt => dt.id);
    setSelectedDocTypeIds(prev => {
      const newIds = new Set(prev);
      groupDocTypeIds.forEach(id => newIds.add(id));
      return Array.from(newIds);
    });
  };

  const handleGroupDeselect = (group: DocumentTypeGroup) => {
    const groupDocTypeIds = new Set(group.document_types.map(dt => dt.id));
    setSelectedDocTypeIds(prev => prev.filter(id => !groupDocTypeIds.has(id)));
  };

  const isGroupFullySelected = (group: DocumentTypeGroup): boolean => {
    return group.document_types.every(dt => selectedDocTypeIds.includes(dt.id));
  };

  const isGroupPartiallySelected = (group: DocumentTypeGroup): boolean => {
    const selected = group.document_types.filter(dt => selectedDocTypeIds.includes(dt.id));
    return selected.length > 0 && selected.length < group.document_types.length;
  };

  const getDocumentType = (id: number): DocumentType | undefined => {
    return documentTypes.find(dt => dt.id === id);
  };

  const validateFile = (file: File, docType: DocumentType | undefined): string | null => {
    if (!docType) return 'Invalid type';
    const maxSize = (docType.maxFileSizeMb || 10) * 1024 * 1024;
    if (file.size > maxSize) return `>${docType.maxFileSizeMb}MB`;
    
    const ext = file.name.split('.').pop()?.toLowerCase().trim() || '';
    if (!ext) return 'No extension';
    
    // Handle allowedFileTypes - could be array, string, or undefined
    let allowedTypes: string[] = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    if (docType.allowedFileTypes) {
      if (Array.isArray(docType.allowedFileTypes)) {
        allowedTypes = docType.allowedFileTypes.map(t => String(t).toLowerCase().replace(/^\./, '').trim());
      } else if (typeof docType.allowedFileTypes === 'string') {
        // Handle if it comes as a comma-separated string or JSON string
        try {
          const parsed = JSON.parse(docType.allowedFileTypes);
          if (Array.isArray(parsed)) {
            allowedTypes = parsed.map(t => String(t).toLowerCase().replace(/^\./, '').trim());
          }
        } catch {
          // Not JSON, might be comma-separated
          allowedTypes = (docType.allowedFileTypes as string).split(',').map(t => t.toLowerCase().replace(/^\./, '').trim());
        }
      }
    }
    
    if (!allowedTypes.includes(ext)) return `Invalid`;
    return null;
  };

  const handleDocTypeToggle = (docTypeId: number) => {
    setSelectedDocTypeIds(prev => 
      prev.includes(docTypeId) ? prev.filter(id => id !== docTypeId) : [...prev, docTypeId]
    );
  };

  const handleLeadSelect = (lead: Lead) => {
    if (mode === 'single') {
      setSelectedLeads([lead]);
      setShowLeadDropdown(false);
    } else {
      if (selectedLeads.find(l => l.id === lead.id)) {
        setSelectedLeads(prev => prev.filter(l => l.id !== lead.id));
      } else if (selectedLeads.length < MAX_BULK_LEADS) {
        setSelectedLeads(prev => [...prev, lead]);
      }
    }
    setLeadSearchTerm('');
  };

  const removeSelectedLead = (leadId: number) => {
    setSelectedLeads(prev => prev.filter(l => l.id !== leadId));
  };

  const filteredLeads = leads.filter(lead => {
    const searchLower = leadSearchTerm.toLowerCase();
    const fullName = `${lead.firstName} ${lead.lastName}`.toLowerCase();
    return (fullName.includes(searchLower) || lead.email.toLowerCase().includes(searchLower)) 
           && !selectedLeads.find(l => l.id === lead.id);
  });

  // Grid cell file handling - supports multiple files
  const handleGridCellFiles = (leadId: number, docTypeId: number, newFiles: FileList | File[]) => {
    const docType = getDocumentType(docTypeId);
    const filesToAdd: FileUpload[] = Array.from(newFiles).map(file => ({
      id: generateFileId(),
      file,
      error: validateFile(file, docType) || undefined
    }));
    
    setGridCells(prev => prev.map(cell => {
      if (cell.leadId === leadId && cell.documentTypeId === docTypeId) {
        return { ...cell, files: [...cell.files, ...filesToAdd] };
      }
      return cell;
    }));
  };

  const removeGridCellFile = (leadId: number, docTypeId: number, fileId: string) => {
    setGridCells(prev => prev.map(cell => {
      if (cell.leadId === leadId && cell.documentTypeId === docTypeId) {
        return { ...cell, files: cell.files.filter(f => f.id !== fileId) };
      }
      return cell;
    }));
  };

  // Single mode file handling - supports multiple files
  const handleSingleFiles = (docTypeId: number, newFiles: FileList | File[]) => {
    const docType = getDocumentType(docTypeId);
    const filesToAdd: FileUpload[] = Array.from(newFiles).map(file => ({
      id: generateFileId(),
      file,
      error: validateFile(file, docType) || undefined
    }));
    
    setSingleUploads(prev => prev.map(upload => {
      if (upload.docTypeId === docTypeId) {
        return { ...upload, files: [...upload.files, ...filesToAdd] };
      }
      return upload;
    }));
  };

  const removeSingleFile = (docTypeId: number, fileId: string) => {
    setSingleUploads(prev => prev.map(upload => {
      if (upload.docTypeId === docTypeId) {
        return { ...upload, files: upload.files.filter(f => f.id !== fileId) };
      }
      return upload;
    }));
  };

  const handleDragOver = useCallback((e: React.DragEvent, cellKey: string) => {
    e.preventDefault();
    setDragOverCell(cellKey);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOverCell(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, leadId: number, docTypeId: number) => {
    e.preventDefault();
    setDragOverCell(null);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      if (mode === 'bulk') {
        handleGridCellFiles(leadId, docTypeId, files);
      } else {
        handleSingleFiles(docTypeId, files);
      }
    }
  }, [mode]);

  const canSubmit = (): boolean => {
    if (selectedLeads.length === 0) return false;
    if (mode === 'single') {
      return singleUploads.some(u => u.files.some(f => !f.error));
    } else {
      return gridCells.some(c => c.files.some(f => !f.error));
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setUploading(true);
    setError(null);
    
    try {
      const api = await import('../services/api').then(m => m.default);
      
      if (mode === 'single') {
        const lead = selectedLeads[0];
        const formData = new FormData();
        formData.append('leadId', lead.id.toString());
        formData.append('source', uploadSource);
        formData.append('autoVerify', autoVerify.toString());
        const items: { documentTypeId: number; fileCount: number }[] = [];
        
        for (const upload of singleUploads) {
          const validFiles = upload.files.filter(f => !f.error);
          if (validFiles.length > 0) {
            items.push({ documentTypeId: upload.docTypeId, fileCount: validFiles.length });
            validFiles.forEach(f => formData.append('files', f.file));
          }
        }
        
        formData.append('items', JSON.stringify(items));
        await api.post('/documents/direct-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const leadsWithFiles = Array.from(new Set(
          gridCells.filter(c => c.files.some(f => !f.error)).map(c => c.leadId)
        ));
        setUploadProgress({ current: 0, total: leadsWithFiles.length });
        
        for (let i = 0; i < leadsWithFiles.length; i++) {
          const leadId = leadsWithFiles[i];
          const leadCells = gridCells.filter(c => c.leadId === leadId && c.files.some(f => !f.error));
          if (leadCells.length === 0) continue;
          
          setUploadProgress({ current: i + 1, total: leadsWithFiles.length });
          const formData = new FormData();
          formData.append('leadId', leadId.toString());
          formData.append('source', uploadSource);
          formData.append('autoVerify', autoVerify.toString());
          const items: { documentTypeId: number; fileCount: number }[] = [];
          
          for (const cell of leadCells) {
            const validFiles = cell.files.filter(f => !f.error);
            if (validFiles.length > 0) {
              items.push({ documentTypeId: cell.documentTypeId, fileCount: validFiles.length });
              validFiles.forEach(f => formData.append('files', f.file));
            }
          }
          
          formData.append('items', JSON.stringify(items));
          await api.post('/documents/direct-upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Compact Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <CloudArrowUpIcon className="w-5 h-5 text-gray-600" />
            <h3 className="text-base font-semibold text-gray-900">Quick Upload</h3>
            
            {!lockLeadSelection && (
              <div className="flex bg-gray-200 rounded-lg p-0.5 ml-4">
                <button
                  onClick={() => { setMode('single'); setSelectedLeads(selectedLeads.slice(0, 1)); }}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                    mode === 'single' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Single
                </button>
                <button
                  onClick={() => setMode('bulk')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                    mode === 'bulk' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Bulk Grid
                </button>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={uploadSource}
              onChange={(e) => setUploadSource(e.target.value)}
              className="text-xs border-gray-300 rounded-md py-1 pl-2 pr-6"
            >
              {UPLOAD_SOURCES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            
            <label className="flex items-center gap-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded-md cursor-pointer hover:bg-green-100 transition">
              <input
                type="checkbox"
                checked={autoVerify}
                onChange={(e) => setAutoVerify(e.target.checked)}
                className="w-3.5 h-3.5 text-green-600 rounded border-green-300 focus:ring-green-500"
              />
              <span className="text-xs font-medium text-green-700">Auto Verify</span>
            </label>
            
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {success ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-gray-900">Upload Complete!</p>
            </div>
          </div>
        ) : (
          <>
            {/* Main Content */}
            <div className="flex-1 overflow-hidden flex">
              {/* Left Sidebar - Selection */}
              <div className="w-64 border-r bg-gray-50 flex flex-col overflow-hidden">
                {/* Lead Selection */}
                <div className="p-3 border-b">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {mode === 'single' ? 'Lead' : `Leads (${selectedLeads.length}/${MAX_BULK_LEADS})`}
                  </label>
                  
                  {lockLeadSelection && selectedLeads[0] ? (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-white rounded border">
                      <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
                        {selectedLeads[0].firstName[0]}{selectedLeads[0].lastName[0]}
                      </div>
                      <span className="text-sm text-gray-900 truncate">
                        {selectedLeads[0].firstName} {selectedLeads[0].lastName}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-2 relative" ref={leadDropdownRef}>
                      <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={leadSearchTerm}
                        onChange={(e) => { setLeadSearchTerm(e.target.value); setShowLeadDropdown(true); }}
                        onFocus={() => setShowLeadDropdown(true)}
                        placeholder="Search..."
                        className="w-full pl-7 pr-2 py-1.5 text-sm border rounded-md"
                      />
                      {showLeadDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredLeads.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-gray-500">No leads</p>
                          ) : (
                            filteredLeads.slice(0, 15).map(lead => (
                              <button
                                key={lead.id}
                                onClick={() => handleLeadSelect(lead)}
                                disabled={mode === 'bulk' && selectedLeads.length >= MAX_BULK_LEADS}
                                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-50"
                              >
                                <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs">
                                  {lead.firstName[0]}{lead.lastName[0]}
                                </div>
                                <span className="truncate">{lead.firstName} {lead.lastName}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {mode === 'bulk' && selectedLeads.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedLeads.map(lead => (
                        <span key={lead.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                          {lead.firstName} {lead.lastName[0]}.
                          <button onClick={() => removeSelectedLead(lead.id)} className="hover:text-blue-900">
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Document Groups & Types */}
                <div className="flex-1 overflow-y-auto p-3">
                  {/* Quick Select Groups */}
                  {documentGroups.length > 0 && (
                    <div className="mb-3 pb-3 border-b">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Quick Select Groups
                      </label>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {documentGroups.map(group => {
                          const isFullySelected = isGroupFullySelected(group);
                          const isPartiallySelected = isGroupPartiallySelected(group);
                          return (
                            <button
                              key={group.id}
                              onClick={() => isFullySelected ? handleGroupDeselect(group) : handleGroupSelect(group)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition ${
                                isFullySelected 
                                  ? 'bg-blue-600 text-white' 
                                  : isPartiallySelected
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              style={!isFullySelected && !isPartiallySelected ? { borderLeft: `3px solid ${group.color}` } : {}}
                              title={`${group.document_types.length} document types`}
                            >
                              {group.name}
                              <span className="opacity-70">({group.document_types.length})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Document Types ({selectedDocTypeIds.length})
                  </label>
                  <div className="mt-2 space-y-1">
                    {documentTypes.map(dt => (
                      <label
                        key={dt.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition ${
                          selectedDocTypeIds.includes(dt.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          selectedDocTypeIds.includes(dt.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {selectedDocTypeIds.includes(dt.id) && <CheckIcon className="w-3 h-3 text-white" />}
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedDocTypeIds.includes(dt.id)}
                          onChange={() => handleDocTypeToggle(dt.id)}
                          className="sr-only"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate">{dt.name}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Content - Upload Area */}
              <div className="flex-1 overflow-auto p-4">
                {selectedLeads.length === 0 || selectedDocTypeIds.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <DocumentTextIcon className="w-10 h-10 mx-auto mb-2" />
                      <p className="text-sm">Select leads and document types to upload</p>
                    </div>
                  </div>
                ) : mode === 'single' ? (
                  /* Single Mode - List with multiple files support */
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {selectedLeads[0]?.firstName[0]}{selectedLeads[0]?.lastName[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{selectedLeads[0]?.firstName} {selectedLeads[0]?.lastName}</p>
                        <p className="text-xs text-gray-500">{selectedLeads[0]?.email}</p>
                      </div>
                    </div>
                    
                    {singleUploads.map(upload => {
                      const docType = getDocumentType(upload.docTypeId);
                      const cellKey = `single-${upload.docTypeId}`;
                      const hasFiles = upload.files.length > 0;
                      
                      return (
                        <div
                          key={upload.docTypeId}
                          className={`p-3 rounded-lg border transition ${
                            dragOverCell === cellKey ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                          }`}
                          onDragOver={(e) => handleDragOver(e, cellKey)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, selectedLeads[0].id, upload.docTypeId)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">{docType?.name}</span>
                            <span className="text-xs text-gray-400">
                              {upload.files.length} file{upload.files.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          
                          {/* File list */}
                          {hasFiles && (
                            <div className="space-y-1 mb-2">
                              {upload.files.map(f => (
                                <div 
                                  key={f.id} 
                                  className={`flex items-center gap-2 px-2 py-1 rounded text-sm ${
                                    f.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                                  }`}
                                >
                                  <CheckCircleIcon className={`w-4 h-4 flex-shrink-0 ${f.error ? 'text-red-500' : 'text-green-500'}`} />
                                  <span className="truncate flex-1">{f.file.name}</span>
                                  <span className="text-xs opacity-70">{formatFileSize(f.file.size)}</span>
                                  {f.error && <span className="text-xs">{f.error}</span>}
                                  <button 
                                    onClick={() => removeSingleFile(upload.docTypeId, f.id)} 
                                    className="p-0.5 hover:bg-red-100 rounded"
                                  >
                                    <XMarkIcon className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Add more files button */}
                          <label className="flex items-center justify-center gap-2 py-2 border border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition">
                            <PlusIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {hasFiles ? 'Add more files' : 'Drop files or click to upload'}
                            </span>
                            <input
                              type="file"
                              multiple
                              onChange={(e) => {
                                if (e.target.files) handleSingleFiles(upload.docTypeId, e.target.files);
                                e.target.value = '';
                              }}
                              className="hidden"
                              accept={docType?.allowedFileTypes.map(t => `.${t}`).join(',')}
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Bulk Mode - Grid Table with multiple files */
                  <div className="overflow-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr>
                          <th className="sticky left-0 z-10 bg-gray-100 p-2 text-left font-medium text-gray-700 border-b border-r min-w-[120px]">
                            Doc Type
                          </th>
                          {selectedLeads.map(lead => (
                            <th key={lead.id} className="p-2 text-center border-b bg-gray-50 min-w-[120px]">
                              <div className="flex flex-col items-center gap-1">
                                <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs">
                                  {lead.firstName[0]}{lead.lastName[0]}
                                </div>
                                <span className="text-xs font-medium truncate max-w-[110px]">
                                  {lead.firstName}
                                </span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDocTypeIds.map(docTypeId => {
                          const docType = getDocumentType(docTypeId);
                          return (
                            <tr key={docTypeId}>
                              <td className="sticky left-0 z-10 bg-white p-2 border-b border-r font-medium">
                                <span className="text-xs truncate block max-w-[110px]" title={docType?.name}>
                                  {docType?.name}
                                </span>
                              </td>
                              {selectedLeads.map(lead => {
                                const cell = gridCells.find(c => c.leadId === lead.id && c.documentTypeId === docTypeId);
                                const cellKey = `${lead.id}-${docTypeId}`;
                                const fileCount = cell?.files.length || 0;
                                const hasError = cell?.files.some(f => f.error);
                                
                                return (
                                  <td key={lead.id} className="p-1 border-b">
                                    <div
                                      className={`min-h-[60px] rounded border-2 transition p-1 ${
                                        dragOverCell === cellKey ? 'border-blue-400 bg-blue-50' :
                                        hasError ? 'border-red-300 bg-red-50' :
                                        fileCount > 0 ? 'border-green-300 bg-green-50' : 'border-dashed border-gray-200'
                                      }`}
                                      onDragOver={(e) => handleDragOver(e, cellKey)}
                                      onDragLeave={handleDragLeave}
                                      onDrop={(e) => handleDrop(e, lead.id, docTypeId)}
                                    >
                                      {/* File pills */}
                                      {fileCount > 0 && (
                                        <div className="flex flex-wrap gap-0.5 mb-1">
                                          {cell?.files.map(f => (
                                            <span 
                                              key={f.id} 
                                              className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-xs ${
                                                f.error ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                              }`}
                                              title={f.file.name}
                                            >
                                              <span className="max-w-[60px] truncate">{f.file.name.split('.')[0]}</span>
                                              <button
                                                onClick={() => removeGridCellFile(lead.id, docTypeId, f.id)}
                                                className="hover:bg-red-200 rounded"
                                              >
                                                <XMarkIcon className="w-3 h-3" />
                                              </button>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {/* Add button */}
                                      <label className="flex items-center justify-center gap-1 py-1 cursor-pointer hover:bg-gray-100 rounded transition">
                                        <PlusIcon className="w-3 h-3 text-gray-400" />
                                        <span className="text-xs text-gray-400">
                                          {fileCount > 0 ? 'Add' : 'Upload'}
                                        </span>
                                        <input
                                          type="file"
                                          multiple
                                          onChange={(e) => {
                                            if (e.target.files) handleGridCellFiles(lead.id, docTypeId, e.target.files);
                                            e.target.value = '';
                                          }}
                                          className="hidden"
                                          accept={docType?.allowedFileTypes.map(t => `.${t}`).join(',')}
                                        />
                                      </label>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {error && (
                  <div className="flex items-center gap-1 text-red-600 text-sm">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    {error}
                  </div>
                )}
                {uploading && uploadProgress.total > 1 && (
                  <span className="text-xs text-gray-500">
                    Uploading {uploadProgress.current}/{uploadProgress.total}...
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={uploading}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit() || uploading}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {uploading ? (
                    <>
                      <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="w-4 h-4" />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DirectDocumentUploadModal;
